import {useQuery} from '@tanstack/react-query';
import {apiClient} from '../../http/query-client';
import {BackendResponse} from '../../http/backend-response/backend-response';
import {PaginatedBackendResponse} from '../../http/backend-response/pagination-response';
import {Product} from '../product';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';

const endpoint = 'billing/products';

export interface FetchProductsResponse extends BackendResponse {
  products: Product[];
}

export function useProducts(loader?: string) {
  return useQuery<FetchProductsResponse>({
    // scope by loader so pages that embed a curated payload don't share a
    // cache entry with generic billing/products consumers
    queryKey: loader ? [endpoint, loader] : [endpoint],
    queryFn: () => fetchProducts(),
    // the page-embedded payload is filtered and ordered by its controller;
    // background refetches of the generic API (different filtering) must not
    // silently replace it mid-session. Spread conditionally: an explicit
    // `staleTime: undefined` would OVERRIDE the client-wide 30s default
    // (own properties win the merge in react-query), not fall back to it.
    ...(loader ? {staleTime: Infinity} : {}),
    initialData: () => {
      if (loader) {
        // @ts-ignore
        return getBootstrapData().loaders?.[loader];
      }
    },
  });
}

function fetchProducts(): Promise<FetchProductsResponse> {
  return apiClient
    // default pagination returns an arbitrary first-15 of the table; ask for
    // everything so visible plans can never fall outside the fetched subset
    .get<PaginatedBackendResponse<Product>>(endpoint, {
      params: {perPage: 100},
    })
    .then(response => {
      return {products: response.data.pagination.data};
    });
}
